import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useTeamManagementStore from '@/stores/teamManagement/teamManagementStore';
import { useUserStore } from '@/stores/user/userStore';
import useModalStore from '@/stores/modal/modalStore';
import featureCheck from '@/helpers/tier/featureCheck';
import CreditsAndUpgrade from '@/v3-dashboard/components/CreditsAndUpgrade';

import PhoneIcon from '@/shared/icons/PhoneIcon';
import DTransition from '@/components/Global/DTransition';
import IconWrapper from '@/shared/components/UI/IconWrapper';
import AiChatbotIcon from '@/shared/icons/AiChatbotIcon';
import UsersIcon from '@/shared/icons/UsersIcon';
import HumanHandoverIcon from '@/shared/icons/HumanHandoverIcon';
import VoiceCallIcon from '@/shared/icons/VoiceCallIcon';
import UsersIconNav from '@/shared/icons/UsersIconNav';
import HashIcon from '@/shared/icons/HashIcon';
import BillingIcon from '@/shared/icons/BillingIcon';


const MainNav = ({ closeMobileNav }) => {

  const location = useLocation();
  const navigate = useNavigate();
  const selectedTeam = useTeamManagementStore((state) => state.selectedTeam);
  const user = useUserStore((state) => state.user);
  const openPlansModal = useModalStore((state) => state.openPlansModal);

  console.log('selectedTeam', selectedTeam?.owner_id, user?.id);
  // Build nav links dynamically
  const buildNavLinks = () => {
    const baseNavLinks = [
      {
        label: 'AI Agents',
        icon: <AiChatbotIcon className="text-v3-purple-primary w-full h-full" />,
        iconPlacement: 'pre',
        active:
          location.pathname === '/' ||
          location.pathname.startsWith('/chatbot'),
        activeFunction: () => {
          return (
            location.pathname === '/' ||
            location.pathname.startsWith('/chatbot')
          );
        },
        href: '/',
        onClick: () => {
          navigate('/');
          closeMobileNav && closeMobileNav();
        },
      },
      ...(!selectedTeam || selectedTeam.owner_id === user.id ? [{
        label: 'Human Handover',
        icon: <HumanHandoverIcon className="text-v3-purple-primary w-full h-full" />,
        iconPlacement: 'pre',
        href: '/human-handover',
        active: location.pathname.startsWith('/human-handover'),
        activeFunction: () => {
          return location.pathname.startsWith('/human-handover');
        },
        onClick: () => {
          navigate('/human-handover');
          closeMobileNav && closeMobileNav();
        },
      }] : []),
      ...(!selectedTeam || selectedTeam.owner_id === user.id ? [{
        label: 'AI Voice Agents',
        icon: <VoiceCallIcon className="text-v3-purple-primary w-full h-full" />,
        iconPlacement: 'pre',
        href: '/voice',
        active: location.pathname.startsWith('/voice'),
        activeFunction: () => {
          return location.pathname.startsWith('/voice');
        },
        onClick: () => {
          navigate('/voice');
          closeMobileNav && closeMobileNav();
        },
      }] : []),
      ...(!selectedTeam || selectedTeam.owner_id === user.id ? [{
        label: 'Phone Numbers',
        icon: <HashIcon className="text-v3-purple-primary w-full h-full" />,
        iconPlacement: 'pre',
        href: '/phone-numbers',
        active: location.pathname.startsWith('/phone-numbers'),
        activeFunction: () => {
          return location.pathname.startsWith('/phone-numbers');
        },
        onClick: () => {
          navigate('/phone-numbers');
          closeMobileNav && closeMobileNav();
        },
      }] : []),
      ...(!selectedTeam || selectedTeam.owner_id === user.id ? [{
        label: 'Billing',
        icon: <BillingIcon className="text-v3-purple-primary w-full h-full" />,
        iconPlacement: 'pre',
        href: '/billing/subscription',
        active: location.pathname.startsWith('/billing/subscription') || location.pathname.startsWith('/billing/add-ons') || location.pathname.startsWith('/billing/usage') || location.pathname.startsWith('/billing/invoices') || location.pathname.startsWith('/billing/payment'),
        activeFunction: () => {
          return location.pathname.startsWith('/billing/subscription') || location.pathname.startsWith('/billing/add-ons') || location.pathname.startsWith('/billing/usage') || location.pathname.startsWith('/billing/invoices') || location.pathname.startsWith('/billing/payment');
        },
        onClick: () => {
          navigate('/billing/subscription');
          closeMobileNav && closeMobileNav();
        },
      }] : []),
      // Add "My Team" nav item at the end when a team is selected
      ...(selectedTeam ? [{
        label: `My Team`,
        icon: <UsersIconNav className="text-v3-purple-primary" />,
        iconPlacement: 'pre',
        active: location.pathname.startsWith('/team-management'),
        activeFunction: () => {
          return location.pathname.startsWith('/team-management');
        },
        href: `/team-management/${selectedTeam.id}`,
        onClick: () => {
          if (featureCheck('team_management', true)) {
            navigate(`/team-management/${selectedTeam.id}`);
          } else {
            navigate('/');
          }
          closeMobileNav && closeMobileNav();
        },
      }] : []),
    ];
    return baseNavLinks;
  };
  
  const [navLinks, setNavLinks] = useState(buildNavLinks());

  // Submenu toggling
  const toggleSubmenu = (label) => {
    setNavLinks((prev) =>
      prev.map((item) =>
        item.label === label
          ? { ...item, submenuOpen: !item.submenuOpen }
          : item
      )
    );
  };

  // Update navLinks based on location changes and selectedTeam
  useEffect(() => {
    const baseNavLinks = buildNavLinks();
    const updatedLinks = baseNavLinks.map((navLink) => {
      const updatedLink = {
        ...navLink,
        active: navLink.activeFunction(),
      };

      if (navLink.submenu) {
        updatedLink.submenu = navLink.submenu.map((subNav) => ({
          ...subNav,
          active: subNav.activeFunction ? subNav.activeFunction() : false,
        }));

        updatedLink.submenuOpen =
          typeof navLink.submenuFunction === 'function'
            ? navLink.submenuFunction()
            : false;
      }

      return updatedLink;
    });
    setNavLinks(updatedLinks);
  }, [location.pathname, selectedTeam]);

  // NavItem component matching ChatbotSidebar design
  const NavItem = ({ item, isActive, onClick }) => (
    <div
      className={`flex pl-[12px] pr-v3-sm py-[10px] h-[52px] md:h-[40px] lg:h-[40px] items-center gap-2 self-stretch rounded-v3-lg cursor-pointer hover:bg-v3-menu-hover ${
        isActive ? 'bg-v3-menu-selected' : ''
      }`}
      onClick={onClick}
    >
      <IconWrapper size={20}>
        {item.icon}
      </IconWrapper>
      <span className="text-v3-black-max text-center font-inter-variable text-v3-h10 font-medium whitespace-nowrap">
        {item.label}
      </span>
    </div>
  );

  return (
  <div className="chatbot_nav_wrapper p-v3-sm md:p-v3-sm flex flex-col gap-0 w-full md:w-[220px] md:h-full">

      {/* Main navigation section */}
      <div className="flex flex-col gap-0 overflow-y-auto no-scrollbar flex-grow">
        {navLinks.map((item) => {
          const isActive = item.active;

          return (
            <div key={item.label} className="relative">
              <NavItem
                item={item}
                isActive={isActive}
                onClick={() => {
                  if (item?.submenu && item.submenu.length > 0) {
                    toggleSubmenu(item.label);
                    item.onClick?.();
                  } else {
                    item.onClick?.();
                  }
                }}
              />

              {/* Submenu transition */}
              <DTransition
                show={item.submenu && item.submenuOpen}
                className="mt-0"
                type="collapse"
              >
                {item?.submenu?.map((subNav) => (
                  <NavItem
                    key={subNav.label}
                    item={subNav}
                    isActive={subNav.active}
                    onClick={subNav.onClick}
                  />
                ))}
              </DTransition>
            </div>
          );
        })}
      </div>

      <div className="mt-auto flex flex-col gap-v3-xs">
        <CreditsAndUpgrade
          used={user?.credits_key?.credits_used}
          total={user?.credits_key?.credits_available}
          onUpgrade={() => openPlansModal()}
        />
      </div>
    </div>
  );
};

export default MainNav;
